import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { AlertTriangle, Clock, XCircle, Lock, LogOut, ShieldAlert } from 'lucide-react';
import { GlobalLoader } from '@/components/common/GlobalLoader';

interface StatusGateProps {
  children: React.ReactNode;
}

export function StatusGate({ children }: StatusGateProps) {
  const { session, profile, tenant, loading, logout, canAccessApp } = useAuthStore();
  const [showTimeoutWarning, setShowTimeoutWarning] = useState(false);

  useEffect(() => {
    if (!loading) {
      setShowTimeoutWarning(false);
      return;
    }
    const timer = setTimeout(() => {
      setShowTimeoutWarning(true);
    }, 6000); // 6 segundos de tolerância

    return () => clearTimeout(timer);
  }, [loading]);

  const handleRetry = () => {
    setShowTimeoutWarning(false);
    useAuthStore.getState().initialize();
  };

  if (loading) {
    return (
      <GlobalLoader
        variant="fullscreen"
        title="Task CRM"
        message="Carregando dados..."
        showTimeoutWarning={showTimeoutWarning}
        onRetry={handleRetry}
        onLogout={() => logout()}
      />
    );
  }

  if (!session) {
    return <Navigate to="/login" replace />;
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0B141A] flex flex-col items-center justify-center p-4 transition-colors">
        <div className="max-w-md w-full bg-white dark:bg-[#111B21] rounded-2xl p-8 border border-gray-200 dark:border-gray-800 shadow-lg text-center space-y-4">
          <div className="w-14 h-14 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center mx-auto">
            <AlertTriangle className="w-7 h-7" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Sem perfil cadastrado</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            Sua conta de autenticação existe, mas nenhum perfil de usuário foi associado. Entre em contato com o suporte para vincular seu cadastro.
          </p>
          <button
            onClick={() => logout()}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium text-sm transition-colors mt-2"
          >
            <LogOut className="w-4 h-4" />
            Sair da conta
          </button>
        </div>
      </div>
    );
  }

  // Super Admin bypasses tenant status checks
  if (profile.role === 'super_admin') {
    return <>{children}</>;
  }

  // Non super admin needs a tenant
  if (!tenant) {
    if (canAccessApp) {
      return <>{children}</>;
    }
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0B141A] flex flex-col items-center justify-center p-4 transition-colors">
        <div className="max-w-md w-full bg-white dark:bg-[#111B21] rounded-2xl p-8 border border-gray-200 dark:border-gray-800 shadow-lg text-center space-y-4">
          <div className="w-14 h-14 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center mx-auto">
            <ShieldAlert className="w-7 h-7" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Configuração Incompleta</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            Sua conta não está associada a nenhuma empresa/organização ativa. Por favor, contate o administrador do sistema.
          </p>
          <button
            onClick={() => logout()}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium text-sm transition-colors mt-2"
          >
            <LogOut className="w-4 h-4" />
            Sair da conta
          </button>
        </div>
      </div>
    );
  }

  if (tenant.status === 'pendente') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0B141A] flex flex-col items-center justify-center p-4 transition-colors">
        <div className="max-w-md w-full bg-white dark:bg-[#111B21] rounded-2xl p-8 border border-gray-200 dark:border-gray-800 shadow-lg text-center space-y-4">
          <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full flex items-center justify-center mx-auto">
            <Clock className="w-7 h-7" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Aguardando Aprovação</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            Sua empresa (<strong className="text-gray-900 dark:text-white">{tenant.nome}</strong>) está aguardando aprovação do administrador do sistema. Você receberá acesso assim que o cadastro for validado.
          </p>
          <button
            onClick={() => logout()}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium text-sm transition-colors mt-2"
          >
            <LogOut className="w-4 h-4" />
            Sair da conta
          </button>
        </div>
      </div>
    );
  }

  if (tenant.status === 'rejeitado') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0B141A] flex flex-col items-center justify-center p-4 transition-colors">
        <div className="max-w-md w-full bg-white dark:bg-[#111B21] rounded-2xl p-8 border border-gray-200 dark:border-gray-800 shadow-lg text-center space-y-4">
          <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded-full flex items-center justify-center mx-auto">
            <XCircle className="w-7 h-7" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Cadastro Rejeitado</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            Seu cadastro foi rejeitado. Contate o suporte para mais detalhes.
          </p>
          {tenant.motivo_rejeicao && (
            <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900/40 rounded-xl text-xs text-red-700 dark:text-red-300 text-left">
              <strong>Motivo:</strong> {tenant.motivo_rejeicao}
            </div>
          )}
          <button
            onClick={() => logout()}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium text-sm transition-colors mt-2"
          >
            <LogOut className="w-4 h-4" />
            Sair da conta
          </button>
        </div>
      </div>
    );
  }

  if (tenant.status === 'suspenso') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0B141A] flex flex-col items-center justify-center p-4 transition-colors">
        <div className="max-w-md w-full bg-white dark:bg-[#111B21] rounded-2xl p-8 border border-gray-200 dark:border-gray-800 shadow-lg text-center space-y-4">
          <div className="w-14 h-14 bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-full flex items-center justify-center mx-auto">
            <Lock className="w-7 h-7" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Conta Suspensa</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            A conta da sua empresa está temporariamente suspensa. Entre em contato com o administrador.
          </p>
          <button
            onClick={() => logout()}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium text-sm transition-colors mt-2"
          >
            <LogOut className="w-4 h-4" />
            Sair da conta
          </button>
        </div>
      </div>
    );
  }

  if (tenant.status === 'cancelado') {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-[#0B141A] flex flex-col items-center justify-center p-4 transition-colors">
        <div className="max-w-md w-full bg-white dark:bg-[#111B21] rounded-2xl p-8 border border-gray-200 dark:border-gray-800 shadow-lg text-center space-y-4">
          <div className="w-14 h-14 bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-400 rounded-full flex items-center justify-center mx-auto">
            <XCircle className="w-7 h-7" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Conta Cancelada</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
            Esta assinatura/conta foi cancelada.
          </p>
          <button
            onClick={() => logout()}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl font-medium text-sm transition-colors mt-2"
          >
            <LogOut className="w-4 h-4" />
            Sair da conta
          </button>
        </div>
      </div>
    );
  }

  if (tenant.status === 'aprovado') {
    return <>{children}</>;
  }

  return <>{children}</>;
}
